<template>
  <div class="session-header-wrapper">
    <div :class="{ inactive: !isSessionInProgress }" class="session-header">
      <div class="avatar-info-container">
        <component
          :is="partnerAvatar"
          class="avatar"
          :class="!isSessionAlive && 'avatar--hidden'"
        />
        <div class="info">
          <template v-if="isSessionEnding">
            <loading-message
              :message="$t('session.sessionHeader.sessionEndingErrorMessage')"
            />
          </template>
          <template v-else-if="isSessionWaitingForVolunteer">
            <loading-message
              :message="
                $t('session.sessionHeader.waitingForVolunteerErrorMessage')
              "
            />
          </template>
          <template v-else-if="isSessionInProgress">
            <div class="volunteer-info">
              <span class="volunteer-name">{{ sessionPartner.firstname }}</span>
              <span class="in-session-label">{{
                $t('session.sessionHeader.inSessionLabel')
              }}</span>
            </div>
          </template>
          <template v-else-if="isSessionOver">
            <template v-if="sessionPartner.firstname">
              <span>
                {{
                  $t('session.sessionHeader.sessionOverMessage', {
                    sessionPartnerFirstName: sessionPartner.firstname,
                  })
                }}
              </span>
            </template>
            <template v-else>
              <span>{{ $t('session.sessionHeader.sessionEnded') }}</span>
            </template>
          </template>
          <template v-else>
            <loading-message
              :message="$t('session.sessionHeader.loadingMessage')"
            />
          </template>
        </div>
      </div>
      <div class="button-container">
        <button
          v-if="user.isVolunteer"
          class="report-btn"
          @click="reportSession"
          type="button"
        >
          {{ $t('session.sessionHeader.reportSessionButton') }}
        </button>

        <button
          v-if="isSessionWaitingForVolunteer"
          @click="end"
          class="end-session-btn"
          type="button"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          v-else-if="isSessionOver"
          @click="goToFeedbackPage"
          class="end-session-btn"
          type="button"
        >
          {{ $t('session.sessionHeader.sessionOverButton') }}
        </button>
        <button v-else @click="end" class="end-session-btn" type="button">
          {{ $t('session.sessionHeader.endSessionButton') }}
        </button>
      </div>
      <button
        v-if="showSessionStatusChip"
        class="session-status-chip"
        :class="sessionStatusChipClass"
        type="button"
        disabled
      >
        {{ sessionStatusChipLabel }}
      </button>
    </div>
    <trouble-matching-modal
      v-if="showTroubleMatchingModal"
      :closeModal="toggleTroubleMatchingModal"
      :endSession="endSession"
      :sessionId="session._id"
    />
    <unmatched-modal
      v-if="showUnmatchedModal"
      :endSession="endSession"
      :sessionId="session._id"
    />
    <audio
      class="audio__volunteer-joined"
      src="@/assets/audio/alert.mp3"
      muted
    />
  </div>
</template>

<script>
// TODO: This file needs to be refactored to remove session logic from this component
import { mapState, mapGetters } from 'vuex'
import SessionService from '@/services/SessionService'
import router from '@/router'
import StudentIcon from '@/assets/student-icon.svg'
import VolunteerIcon from '@/assets/volunteer-icon.svg'
import ChatBotIcon from '@/assets/chat-bot-icon.svg'
import LoadingMessage from '@/components/LoadingMessage.vue'
import TroubleMatchingModal from '@/views/SessionView/TroubleMatchingModal.vue'
import UnmatchedModal from '@/views/SessionView/UnmatchedModal.vue'
import sendWebNotification from '@/utils/send-web-notification'
import i18n from '@/i18n'

/**
 * @todo {1} Refactoring candidate: use a modal instead.
 */
export default {
  data() {
    return {
      connectionMsg: '',
      connectionMsgType: '',
      reconnectAttemptMsg: '',
      isSessionEnding: false,
      showTroubleMatchingModal: false,
      showUnmatchedModal: false,
      hasSeenTroubleMatchingModal: false,
      isWaitingIntervalId: null,
    }
  },
  components: {
    LoadingMessage,
    TroubleMatchingModal,
    UnmatchedModal,
  },
  mounted() {
    // Show a modal if a student has been waiting too long to get matched with a volunteer
    if (!this.user.isVolunteer) {
      /**
       * There's a re-render that is triggered when fetching for the current session.
       * If the modal is shown before this re-render occurs it will not display on
       * the screen. Set a timeout to display the modal after those initial re-renders
       **/
      setTimeout(() => {
        this.isWaitingTooLong()
      }, 500)

      this.isWaitingIntervalId = setInterval(() => {
        this.isWaitingTooLong()
      }, 1000 * 60)
    }
  },
  beforeDestroy() {
    if (!this.isRecapSocketUpdatesActive) this.$socket.disconnect()
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      session: state => state.user.session,
    }),
    ...mapGetters({
      sessionPartner: 'user/sessionPartner',
      isSessionAlive: 'user/isSessionAlive',
      isSessionWaitingForVolunteer: 'user/isSessionWaitingForVolunteer',
      isSessionInProgress: 'user/isSessionInProgress',
      isSessionOver: 'user/isSessionOver',
      isChatbotActive: 'featureFlags/isChatbotActive',
      isSessionRecapDmsActive: 'featureFlags/isSessionRecapDmsActive',
      isRecapSocketUpdatesActive: 'featureFlags/isRecapSocketUpdatesActive',
    }),

    partnerAvatar() {
      if (this.isSessionWaitingForVolunteer) return ChatBotIcon
      // show the current user their partner's avatar
      if (this.user.isVolunteer) return StudentIcon
      else return VolunteerIcon
    },
    showSessionStatusChip() {
      return (
        this.isSessionInProgress ||
        this.isSessionWaitingForVolunteer ||
        this.isSessionOver
      )
    },
    sessionStatusChipLabel() {
      if (this.isSessionInProgress) {
        return this.$t('session.sessionHeader.inSessionLabel')
      }

      return this.$t('session.sessionHeader.notInSessionLabel')
    },
    sessionStatusChipClass() {
      return this.isSessionInProgress
        ? 'session-status-chip--active'
        : 'session-status-chip--inactive'
    },
  },
  methods: {
    end() {
      if (this.isSessionWaitingForVolunteer) {
        const shouldEndSession = window.confirm(
          this.$root.$t(
            'session.sessionHeader.waitingForVolunteerShouldEndSession'
          )
        )

        if (!shouldEndSession) {
          return
        }
      } else {
        // Only ask for confirmation if session hasn't been ended by other user
        const shouldEndSession = this.isSessionAlive
          ? window.confirm(
              this.$root.$t('session.sessionHeader.shouldEndSession')
            )
          : true

        // Early exit if user didn't confirm
        if (!shouldEndSession) {
          return
        }
      }

      if (this.isSessionEnding) {
        return
      }
      this.isSessionEnding = true
      this.endSession()
    },
    endSession() {
      const sessionId = this.session._id

      SessionService.endSession(this, sessionId)
        .then(() => {
          this.$store.dispatch('user/sessionDisconnected')
          if (!this.isSessionRecapDmsActive) this.goToFeedbackPage()
          // Do not send the user directly to the feedback page if they can leave DMs
          this.isSessionEnding = false
        })
        .catch(this.alertCouldNotEnd)
    },
    reportSession() {
      this.$store.dispatch('app/modal/show', {
        component: 'ReportSessionModal',
        data: {
          showTemplateButtons: false,
          currentSession: this.session,
        },
      })
    },
    alertCouldNotEnd() {
      this.isSessionEnding = false
      window.alert(this.$root.$t('session.sessionHeader.alertCouldNotEnd'))
    },
    tryReconnect() {
      // socket must be closed before reopening for automatic reconnections
      // to resume
      this.$socket.close()
      this.$socket.open()
      this.reconnectAttemptMsg = this.$root.$t(
        'session.sessionHeader.reconnectAttemptMsg'
      )
      this.$emit('try-clicked')
    },
    connectionSuccess() {
      this.connectionMsg = ''
      this.reconnectAttemptMsg = ''
      this.connectionMsgType = ''
    },
    isAbsentUser() {
      const { student, volunteer } = this.session
      if (!volunteer) return true

      const messages = this.getMessagesAfterVolunteerJoined()
      let isAbsentStudent = true
      let isAbsentVolunteer = true
      for (const message of messages) {
        if (message.user === student._id) isAbsentStudent = false
        if (message.user === volunteer._id) isAbsentVolunteer = false
        if (!isAbsentStudent && !isAbsentVolunteer) break
      }
      return isAbsentStudent || isAbsentVolunteer
    },
    getMessagesAfterVolunteerJoined() {
      return this.session.messages.filter(
        message =>
          new Date(message.createdAt).getTime() >=
          new Date(this.session.volunteerJoinedAt).getTime()
      )
    },
    goToFeedbackPage() {
      // redirect to the home page if there is an absent user
      // or if the student was not paired with a tutor
      if (this.isAbsentUser())
        return this.$router.push(`/${i18n.locale}/dashboard`)

      router.push(`/${i18n.locale}/feedback/${this.session._id}`)
    },
    toggleTroubleMatchingModal() {
      if (this.isChatbotActive) this.showTroubleMatchingModal = false
      else this.showTroubleMatchingModal = !this.showTroubleMatchingModal
    },
    toggleUnmatchedModal() {
      this.showUnmatchedModal = !this.showUnmatchedModal
    },
    isWaitingTooLong() {
      if (this.session.volunteer) {
        clearInterval(this.isWaitingIntervalId)
        this.showTroubleMatchingModal = false
        this.showUnmatchedModal = false
        return
      }

      const fifteenMins = 1000 * 60 * 15
      const fifteenMinsFromSessionStart =
        new Date(this.session.createdAt).getTime() + fifteenMins
      const fortyFiveMinsFromSessionStart =
        fifteenMinsFromSessionStart + fifteenMins * 2

      if (Date.now() >= fortyFiveMinsFromSessionStart) {
        // Students must end their session after 45 minutes of waiting
        this.toggleUnmatchedModal()
        clearInterval(this.isWaitingIntervalId)
      } else if (
        Date.now() >= fifteenMinsFromSessionStart &&
        !this.hasSeenTroubleMatchingModal
      ) {
        this.toggleTroubleMatchingModal()
        this.hasSeenTroubleMatchingModal = true
      }
    },
  },
  sockets: {
    connect_error() {
      this.connectionMsg = this.$root.$t(
        'session.sessionHeader.connectErrorMessage'
      )
      this.connectionMsgType = 'warning'
    },
    connect_timeout() {
      this.connectionMsg = this.$root.$t(
        'session.sessionHeader.connectTimeoutMessage'
      )
      this.connectionMsgType = 'warning'
    },
    reconnect_attempt() {
      this.reconnectAttemptMsg = this.$root.$t(
        'session.sessionHeader.reconnectAttemptMessage'
      )
    },
    connect() {
      this.connectionSuccess()
    },
  },
  watch: {
    // Close possibly open modals that are triggered by a long waiting period
    // and clear the isWaiting interval when a volunteer joins the session
    async isSessionWaitingForVolunteer(value, prevValue) {
      if (!value && prevValue && !this.session.endedAt) {
        this.showTroubleMatchingModal = false
        this.showUnmatchedModal = false
        clearInterval(this.isWaitingIntervalId)
        try {
          const volunteerJoinedAudio = document.querySelector(
            '.audio__volunteer-joined'
          )
          // Unmuting the audio allows us to bypass the need for user interaction with the DOM before playing a sound
          volunteerJoinedAudio.muted = false
          await volunteerJoinedAudio.play()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('Unable to play audio')
        }
        sendWebNotification(
          this.$root.$t('session.sessionHeader.webNotification'),
          {
            body: this.$root.$t('session.sessionHeader.webNotificationBody', {
              sessionPartnerFirstName: this.sessionPartner.firstname,
            }),
          }
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.session-header-wrapper {
  height: 100%;
}

.session-header {
  position: relative;
  height: 100%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  padding: 0 20px;
  text-align: left;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @include breakpoint-below('medium') {
    border-radius: 0 0 24px 24px;
    height: auto;
    min-height: 76px;
    padding: calc(20px + env(safe-area-inset-top)) 18px
      calc(16px + env(safe-area-inset-bottom));
    box-shadow: 0 14px 28px rgba(9, 43, 106, 0.22);
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }
}

h1 {
  margin: 0;
  text-align: left;
  font-size: 36px;
  line-height: 42px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 18px;
  flex-shrink: 0;

  &--hidden {
    visibility: hidden;

    @include breakpoint-below('large') {
      display: none;
    }
  }
}

.info {
  // Padding removed to bring it close to avatar
  padding-left: 0;
  color: white;
  font-weight: 600;
  font-size: 16px;
  max-width: 600px;

  @include breakpoint-below('large') {
    line-height: 1;
  }

  @include breakpoint-below('medium') {
    color: rgba(255, 255, 255, 0.95);
    font-size: 15px;
  }
}

.volunteer-info {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.25;

  @include breakpoint-below('medium') {
    // Keeps name and status label together
    flex-direction: column;
    align-items: flex-start; // Aligns text to the start
    gap: 4px;
    width: 100%;
  }
}

.volunteer-name {
  font-weight: 600;
  font-size: 18px;

  @include breakpoint-below('medium') {
    font-size: 17px;
  }
}

.in-session-label {
  font-weight: 400;
  font-size: 12px;

  @include breakpoint-below('medium') {
    font-size: 12px;
    opacity: 0.8;
    // The "in session" label is hidden on mobile in the original design
    display: none;
  }
}

.button-container {
  display: flex;
  align-items: center;

  @include breakpoint-below('medium') {
    width: 100%;
    justify-content: center;
    gap: 10px;
  }
}

.report-btn {
  display: block;
  border: none;
  background-color: inherit;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  padding: 0 5px;
  margin-right: 10px;

  &:hover {
    color: #e8e8e8;
  }

  @include breakpoint-below('medium') {
    padding: 10px 16px;
    margin-right: 0;
    border-radius: 9999px;
    background: rgba(12, 53, 126, 0.35);
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
      color: white;
      transform: translateY(-1px);
    }
  }
}

.end-session-btn {
  display: inline;
  background-color: inherit;
  font-weight: 500;
  cursor: pointer;
  border: solid 1px #fff;
  color: #fff;
  font-size: 16px;
  line-height: 125%;
  padding: 12px 24px;
  border-radius: 9999px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @include breakpoint-below('large') {
    line-height: 1;
    padding: 9px;
    min-height: 46px;
  }

  @include breakpoint-below('medium') {
    flex: 1;
    min-height: 44px;
    padding: 12px 20px;
    font-size: 15px;
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: #fff;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
    transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
      transform: translateY(-1px);
    }
  }
}

.session-header.inactive {
  background-color: #7a91a8;
}

.connection-message {
  padding: 3px;
  background-color: #858585;
  color: #fff;
  text-align: center;
  font-weight: 600;
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;

  &.warning {
    background-color: #ffde5e;
    color: #000;
  }

  &.success {
    background-color: #fff;
    color: #000;
  }

  .connection-try-again {
    border: 0;
    background: none;
    padding: 0;
    margin: 0;
    text-decoration: underline;
  }
}

.avatar-info-container {
  display: flex;
  align-items: center;
  gap: 12px; // Controls space between avatar and info

  @include breakpoint-below('medium') {
    width: 100%;
    // Removed rules that separated avatar and info
  }
}

.volunteer-info br {
  display: none; // br is not needed anymore
}

.session-status-chip {
  display: none;
  border: none;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 9px 18px;
  pointer-events: none;
  cursor: default;

  @include breakpoint-below('medium') {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    position: absolute;
    top: calc(10px + env(safe-area-inset-top));
    right: calc(18px + env(safe-area-inset-right));
    box-shadow: 0 12px 28px rgba(8, 35, 85, 0.24);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &::before {
      content: '';
      display: block;
      width: 9px;
      height: 9px;
      border-radius: 9999px;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
    }

    &.session-status-chip--active {
      background: linear-gradient(140deg, #4ef6a6, #18c47b);
      color: #033924;
      box-shadow: 0 12px 28px rgba(31, 174, 115, 0.28);

      &::before {
        background: rgba(255, 255, 255, 0.95);
      }
    }

    &.session-status-chip--inactive {
      background: linear-gradient(140deg, #ff8a8a, #f55568);
      color: #5c071c;
      box-shadow: 0 12px 28px rgba(207, 60, 84, 0.28);

      &::before {
        background: rgba(255, 255, 255, 0.9);
      }
    }
  }
}

.audio__volunteer-joined {
  display: none;
}

html.is-rtl {
  .session-header {
    text-align: right;
  }

  h1 {
    text-align: right;
  }

  .info {
    padding-right: 0;
    padding-left: unset;
  }

  .avatar-info-container {
    // RTL specific styles can be added if needed
  }

  .report-btn {
    margin-left: 10px;
    margin-right: unset;

    @include breakpoint-below('medium') {
      margin-left: 0;
    }
  }

  .connection-message {
    right: 0;
    left: unset;
  }
}
</style>
